import { DELEGATION_SUB_TYPES } from '@/helper/constants';
import { yupValidator } from 'lib/yup-validator';
import AlertModal from 'molecules/AlertModal';
import ButtonGroup from 'molecules/ButtonGroup';
import Drawer from 'molecules/Drawer';
import Empty from 'molecules/Empty';
import { useSession } from 'next-auth/react';
import AddDelegate from 'organisms/AddDelegate';
import { accessOptions } from 'organisms/Delegate';
import { RenderDelegates } from 'organisms/RenderDelegates';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createDelegate, getDelegatedByUsers, getDelegates, removeDelegate } from 'services/delegate.service';
import { createImpersonationService } from 'services/impersonation.service';
import useGlobalToastStore from 'store/globalToastStore';
import useSessionStore from 'store/sessionStore';
import * as yup from 'yup';


const getRefactoredResource = (resource) => {
    const {
        employeeCode, name, designation, subServiceArea, serviceArea, employeeFunction, id
    } = resource;
    return {
        id,
        userpic: '',
        employeeCode,
        name,
        designation,
        subServiceArea,
        serviceArea,
        employeeFunction
    };
};

export const delegatesDataMaker = (delegates) => delegates.map((delegateData) => {
    const { delegatedBy, type, id, activityId, spineCodes, delegatePrimaryId, firmInitiativeCodes, subDelegates, workPackageTitle = null, podName = null } = delegateData ?? {};
    const { resource } = delegatedBy ?? {};
    return {
        ...getRefactoredResource(resource),
        access: accessOptions.find((access) => access.value === type),
        delegatePrimaryId,
        activityId,
        spineCodes,
        firmInitiativeCodes,
        workPackageTitle,
        podTitle: podName,
        delegation: delegateData,
        delegationAccess: false,
        canDelegateAllIntakeRequest: false,
        isSpecificIntakeRequest: false,
        delegateSubType: subDelegates?.map(subDelegate => subDelegate?.subType) || []
    };
});


export const delegateAccessDataMaker = (delegates) => delegates.map((delegateData) => {
    const { delegate, type, delegateId } = delegateData;
    const delegateSessionType = ['specificProject', 'specificRequisition', 'specificFirmInitiative'];
    const delegateWorkPackageTitle = delegateData?.specificWorkPackage?.code ?? null;
    const delegatePodName = delegateData?.specificPod?.name ?? null;
    const activityCode = delegateWorkPackageTitle || delegatePodName || delegateData?.specificProject?.spineCode || delegateData?.specificRequisition?.requisitionCode;
    const firmInitiativeCode = delegateData?.specificFirmInitiative?.initiativeCode ?? null;

    const subDelegates = delegateData.subDelegates || [];
    const subType = subDelegates.length ? subDelegates[0].subType : null;
    const subActivityIds = subDelegates.map(s => s.subActivityId).filter(Boolean);
    const intakeRequestCode = subDelegates.find(s => s.subType === DELEGATION_SUB_TYPES.SPECIFIC_INTAKE_REQUEST && s.intakeRequest) ? subDelegates.find(s => s.subType === DELEGATION_SUB_TYPES.SPECIFIC_INTAKE_REQUEST).intakeRequest.code : null;

    return {
        ...getRefactoredResource({ ...delegate.resource, user: { id: delegateId } }),
        access: accessOptions.find((access) => access.value === type),
        delegatePrimaryId: delegates.map((delegate) => delegateSessionType.includes(type) && delegate?.id),
        delegation: delegateData,
        isDelegate: delegates.length ? true : false,
        spineCodes: delegateSessionType.includes(type) ? [activityCode] : [],
        workPackageTitle: type === 'specificWorkPackage' ? delegateWorkPackageTitle : null,
        workPackageId: type === 'specificWorkPackage' ? delegateData?.specificWorkPackage?.id : null,
        podTitle: type === 'specificPod' ? delegatePodName : null,
        podId: type === 'specificPod' ? delegateData?.specificPod?.id : null,
        firmInitiativeCodes: delegateSessionType.includes(type) && firmInitiativeCode ? [firmInitiativeCode] : [],
        delegationAccess: true,
        delegateAllIntakeRequests: type === 'project' && subType === DELEGATION_SUB_TYPES.ALL_INTAKE_REQUEST,
        canDelegateAllIntakeRequest: type === 'project',
        isAllIntakeRequest: type === 'project' && subType === DELEGATION_SUB_TYPES.ALL_INTAKE_REQUEST,
        isSpecificIntakeRequest: type === 'specificProject' && subType === DELEGATION_SUB_TYPES.SPECIFIC_INTAKE_REQUEST,
        specificIntakeRequestCode: intakeRequestCode,
        specificIntakeRequestCount: subActivityIds?.length || 0,
        subType

    };
});


function Delegates({ onClose }) {
    const { setEndSession } = useSessionStore();
    const { setToastData } = useGlobalToastStore();
    const [delegates, setDelegates] = useState([]);
    const session = useSession();
    const [isEdit, setIsEdit] = useState(false);
    const [showDialog, setShowDialog] = useState('');
    const [selected, setSelected] = useState({});
    const isAuditUser = (session?.data?.user?.function === 'Audit & Assurance' && session?.data?.user?.serviceArea === 'Audit') || session?.data?.user?.isAuditRM || session?.data?.user?.grade === 'DM';
    const isSlOrSCLead = session?.data?.user?.role?.includes('service_line_lead') || session?.data?.user?.role?.includes('service_category_lead')
    const isPodLead = session?.data?.user?.role?.includes('pod_lead');

    const accessOptionsForAdd = [
        {
            label: 'All access',
            value: 'all'
        },
        {
            label: 'Project',
            value: 'project'
        },
        ...(isSlOrSCLead ? [{
            label: 'work packages',
            value: 'workPackage'
        }] : []),
        ...(isPodLead ? [{
            label: 'PODs',
            value: 'pod'
        }] : []),
        {
            label: 'Requisition',
            value: 'requisition'
        },
        {
            label: 'Profile',
            value: 'profile'
        },
        {
            label: 'Firm Initiative',
            value: 'firmInitiative'
        },
        ...(isAuditUser ? [{
            label: 'Stock/Cash Count',
            value: 'stockCount'
        }] : [])
    ];

    const {
        control, register, watch, reset, setValue
    } = useForm({
        resolver: yupValidator(
            yup.object().shape({
                access: yup.object().required(),
                selected: yup.object().required(),
                delegateAllIntakeRequests: yup.bool().notRequired()
            })
        ),
        defaultValues: {
            selected: {}
        }
    });

    const fetchDelegations = async () => {
        const res = await getDelegatedByUsers();
        if (res.status) {
            const specificProjectDelegates = res.entity.filter(delegate => delegate.type === 'specificProject');
            const specificRequsitionDelegates = res.entity.filter(delegate => delegate.type === 'specificRequisition');
            const specificFirmInitiativeDelegates = res.entity.filter(delegate => delegate.type === 'specificFirmInitiative');
            const specificPodDelegates = res.entity.filter(delegate => delegate.type === 'specificPod');
            const requsitionDelegates = res.entity.filter(delegate => delegate.type === 'requisition');
            const specRequsition = requsitionDelegates.length ? requsitionDelegates[0] : {}
            const specificWorkPackageDelegates = res.entity.filter(delegate => delegate.type === 'specificWorkPackage');

            const projectDelegatedBy = specificProjectDelegates.reduce((acc, delegation) => {
                const { delegatedById } = delegation
                if (!acc[delegatedById]) {
                    acc[delegatedById] = []
                }

                acc[delegatedById].push(delegation)

                return acc;
            }, {})

            const requisitionDelegatedBy = specificRequsitionDelegates.reduce((acc, delegation) => {
                const { delegatedById } = delegation
                if (!acc[delegatedById]) {
                    acc[delegatedById] = []
                }

                acc[delegatedById].push(delegation)

                return acc;
            }, {})

            const firmInitiativeDelegatedBy = specificFirmInitiativeDelegates.reduce((acc, delegation) => {
                const { delegatedById } = delegation
                if (!acc[delegatedById]) {
                    acc[delegatedById] = []
                }
                acc[delegatedById].push(delegation)

                return acc;
            }, {})

            const workPackageDelegatedBy = specificWorkPackageDelegates.reduce((acc, delegation) => {
                const { delegatedById } = delegation
                if (!acc[delegatedById]) {
                    acc[delegatedById] = []
                }
                acc[delegatedById].push(delegation)

                return acc;
            }, {})

            const delegatedByUser = Object.keys(projectDelegatedBy);
            const reqDelegatedByUser = Object.keys(requisitionDelegatedBy);
            const firmInitiativeDelegatedByUser = Object.keys(firmInitiativeDelegatedBy);
            const workPackageDelegatedByUser = Object.keys(workPackageDelegatedBy);

            const combinedSpecificProject = delegatedByUser.map((userId) => {
                return projectDelegatedBy[userId].reduce((acc, delegate) => {
                    acc.delegatePrimaryId = acc.delegatePrimaryId || [];
                    if (specRequsition.id !== undefined && specRequsition.id !== null) {
                        acc.delegatePrimaryId.push(specRequsition.id);
                    }
                    acc.delegatePrimaryId.push(delegate.id);
                    acc.delegateId = delegate.delegateId;
                    acc.delegatedById = delegate.delegatedById;
                    acc.type = delegate.type;
                    acc.spineCodes = acc.spineCodes || [];
                    acc.spineCodes.push(delegate?.specificProject?.spineCode);
                    acc.activityId = acc.activityId || [];
                    acc.activityId.push(delegate.activityId);
                    acc.delegatedBy = delegate.delegatedBy;
                    acc.subDelegates = delegate.subDelegates

                    return acc;
                }, {});
            });



            const combinedSpecificRequisition = reqDelegatedByUser.map((userId) => {
                return requisitionDelegatedBy[userId].reduce((acc, delegate) => {
                    acc.delegatePrimaryId = acc.delegatePrimaryId || [];
                    acc.delegatePrimaryId.push(delegate.id);
                    acc.delegateId = delegate.delegateId;
                    acc.delegatedById = delegate.delegatedById;
                    acc.type = delegate.type;
                    acc.spineCodes = acc.spineCodes || [];
                    acc.spineCodes.push(delegate?.specificRequisition?.requisitionCode);
                    acc.activityId = acc.activityId || [];
                    acc.activityId.push(delegate.activityId);
                    acc.delegatedBy = delegate.delegatedBy;
                    acc.subDelegates = delegate.subDelegates;
                    return acc;
                }, {});
            });

            const combinedSpecificFirmInitiative = firmInitiativeDelegatedByUser.map((userId) => {
                return firmInitiativeDelegatedBy[userId].reduce((acc, delegate) => {
                    acc.delegatePrimaryId = acc.delegatePrimaryId || [];
                    acc.delegatePrimaryId.push(delegate.id);
                    acc.delegateId = delegate.delegateId;
                    acc.delegatedById = delegate.delegatedById;
                    acc.type = delegate.type;
                    acc.spineCodes = acc.spineCodes || [];
                    acc.spineCodes.push(delegate?.specificRequisition?.requisitionCode);
                    acc.spineCodes = acc.spineCodes.filter(Boolean);
                    acc.firmInitiativeCodes = acc.firmInitiativeCodes || [];
                    acc.firmInitiativeCodes.push(delegate?.specificFirmInitiative?.initiativeCode);
                    acc.activityId = acc.activityId || [];
                    acc.activityId.push(delegate.activityId);
                    acc.delegatedBy = delegate.delegatedBy;
                    acc.subDelegates = delegate.subDelegates;
                    return acc;
                }, {});
            });


            const combinedSpecificWorkPackage = workPackageDelegatedByUser.map((userId) => {
                return workPackageDelegatedBy[userId].reduce((acc, delegate) => {
                    acc.delegatePrimaryId = acc.delegatePrimaryId || [];
                    acc.delegatePrimaryId.push(delegate.id);
                    acc.delegateId = delegate.delegateId;
                    acc.delegatedById = delegate.delegatedById;
                    acc.type = delegate.type;
                    acc.spineCodes = acc.spineCodes || [];
                    acc.workPackageTitle = delegate?.specificWorkPackage?.code;
                    acc.activityId = acc.activityId || [];
                    acc.activityId.push(delegate.activityId);
                    acc.delegatedBy = delegate.delegatedBy;

                    return acc;
                }, {});
            });

            const podDelegatedBy = specificPodDelegates.reduce((acc, delegation) => {
                const { delegatedById } = delegation
                if (!acc[delegatedById]) {
                    acc[delegatedById] = []
                }

                acc[delegatedById].push(delegation)

                return acc;
            }, {})

            const podDelegatedByUser = Object.keys(podDelegatedBy);

            const combinedSpecificPod = podDelegatedByUser.map((userId) => {
                return podDelegatedBy[userId].reduce((acc, delegate) => {
                    acc.delegatePrimaryId = acc.delegatePrimaryId || [];
                    acc.delegatePrimaryId.push(delegate.id);
                    acc.delegateId = delegate.delegateId;
                    acc.delegatedById = delegate.delegatedById;
                    acc.type = delegate.type;
                    acc.spineCodes = acc.spineCodes || [];
                    acc.activityId = acc.activityId || [];
                    acc.activityId.push(delegate.activityId);
                    acc.delegatedBy = delegate.delegatedBy;
                    acc.podName = delegate?.specificPod?.podId;

                    return acc;
                }, {});
            });

            const otherDelegates = res.entity.filter(delegate => !['specificProject', 'specificRequisition', 'specificFirmInitiative', 'specificWorkPackage', 'specificPod'].includes(delegate.type));

            const delegations = [...otherDelegates, ...combinedSpecificProject, ...combinedSpecificRequisition, ...combinedSpecificFirmInitiative, ...combinedSpecificWorkPackage, ...combinedSpecificPod].filter((delegate) => Object.keys(delegate).length);

            setDelegates(delegatesDataMaker(delegations));
        }
    };

    const fetchAccessDelegations = async () => {
        const res = await getDelegates();
        if (res.status) {
            setDelegates(prevDelegates => [...prevDelegates, ...delegateAccessDataMaker(res.entity)]);
        }
    };

    const fetchAllDelegations = () => {
        fetchDelegations();
        fetchAccessDelegations();
    }

    useEffect(() => {
        fetchAllDelegations()
    }, []);

    const onLogin = async (data) => {
        const result = await createImpersonationService({ resourceId: data.id, delegatePrimaryId: data.delegatePrimaryId, validate: false });

        if (result.status) {
            session.update({
                loginOnBehalf: true,
                resourceId: data.id,
                impersonatingUserId: session.data.user.id,
                logoutOnBehalf: null,
                isDelegate: true,
                delegateType: data.access.value,
                delegatePrimaryId: data.delegatePrimaryId,
                activityId: data?.activityId,
                delegateSubType: data?.delegateSubType
            });
            onClose && onClose();
            setToastData({ show: true, message: `Successfully logged as ${data.name}` });
            setEndSession({ show: true, color: 'blue' });
            setTimeout(() => {
                setToastData({});
            }, 2000);
            setTimeout(() => {
                if (['specificProject', 'project'].includes(data.access.value)) {
                    window.location.href = `/projects`;
                } else if (['specificRequisition', 'requisition'].includes(data.access.value)) {
                    window.location.href = `/requisitions`;
                } else if (['workPackage', 'specificWorkPackage'].includes(data.access.value)) {
                    window.location.href = `/work-packages`;
                } else if (['pod', 'specificPod'].includes(data.access.value)) {
                    window.location.href = `/assigned-pods`;
                } else {
                    window.location.href = '/';
                }
            }, 2000);
        } else {
            setToastData({ show: true, message: result.errorMessage, type: 'error' });
        }
    };

    const onSwitchProfile = (row) => {
        console.log('onSwitchProfile', row);
        onClose();
        onLogin(row);
    };


    const actions = [
        {
            label: 'Edit',
            onSelectedItem: (row) => {
                setValue('selected', row);
                setValue('access', row.access);
                if (row.canDelegateAllIntakeRequest || row.access?.value === 'project') {
                    setValue('delegateAllIntakeRequests', row.delegateAllIntakeRequests ?? false);
                }
                setIsEdit(true);
            }
        },
        {
            label: 'Revoke access',
            onSelectedItem: (row) => {
                setSelected(row);
                setShowDialog('revoke-access');
            }
        }
    ];

    const handleCreateDelegate = async (data) => {
        const delegateDetails = {
            type: data.access.value,
            delegateId: data?.resource?.delegation?.delegateId,
            subType: data.delegateAllIntakeRequests ? DELEGATION_SUB_TYPES.ALL_INTAKE_REQUEST : null,
            subActivityIds: []
        };
        const res = await createDelegate(delegateDetails);
        if (res.status) {
            setToastData({ show: true, message: 'Activity delegated successfully ' });
            fetchAllDelegations();
        } else {
            setToastData({ show: true, message: 'Failed to delegate activity', type: 'error' });
        }
    };

    const onDelegate = (data) => {
        handleCreateDelegate(data);
    };

    const buttons = [
        {
            label: 'Cancel',
            kind: 'primary',
            onClick: () => {
                onClose();
            },
            size: 'l'
        },
        {
            label: 'Update',
            kind: 'primaryLoud',
            onClick: () => {
                if (isEdit) {
                    if (!watch('selected')?.id) {
                        setToastData({ show: true, message: 'Please select resource to delegate activity', type: 'error' });
                        return;
                    }
                    if (!watch('access')?.value) {
                        setToastData({ show: true, message: 'Please select access to delegate activity', type: 'error' });
                        return;
                    }

                    onDelegate({ resource: watch('selected'), access: watch('access'), delegateAllIntakeRequests: watch('delegateAllIntakeRequests') ?? false }, isEdit);
                    setIsEdit(false);
                    reset();
                } else {
                    onClose();
                }
            },
            size: 'l',
            className: 'button-custom-text'
        }
    ];


    const handleRemoveDelegate = async ({ id, setShowDialog }) => {
        const res = await removeDelegate({ id, isOneRecord: true });
        if (res.status) {
            setDelegates((prev) => prev.filter((delegate) => delegate.delegation.id !== id));
            setToastData({ show: true, message: 'Delegation revoked successfully ' });
            setShowDialog('')
        } else {
            setTimeout(() => {
                setToastData({});
            }, 2000);
        }
    };

    const onRevokeAccess = (data, setShowDialog) => {
        handleRemoveDelegate({ id: data.delegation.id, setShowDialog });
    };


    return (
        <Drawer
            show={true}
            title={isEdit ? 'Edit Delegation' : 'Delegations'}
            isFooter={isEdit ? true : false}
            onClose={onClose}
            headerMargin=''
            footer={<ButtonGroup buttons={buttons} />}
        >
            {
                !isEdit && (
                    delegates.length > 0 ? (
                        delegates.map((row, i) => (
                            <RenderDelegates
                                row={row}
                                key={i}
                                isDelegated={row?.delegationAccess}
                                isClickable={!row?.delegationAccess}
                                onClick={() => {
                                    if (!session?.data?.impersonatingUserId && !row?.delegationAccess) {
                                        onSwitchProfile(row);
                                    }
                                }}
                                actions={row?.delegationAccess ? actions : []}
                            />
                        ))
                    ) : (
                        <Empty
                            message="No delegates"
                            isShowCta={false}
                        />
                    )
                )
            }

            {
                isEdit && <AddDelegate
                    isEdit={isEdit}
                    updateResources={() => { }}
                    searchResults={[]}
                    accessOptions={accessOptionsForAdd}
                    control={control}
                    register={register}
                    valueChanged={() => { }}
                    watch={watch}
                    setValue={setValue}
                />
            }

            <AlertModal
                title='Revoke Access'
                description={`Are you sure want to revoke access of ${selected?.name}?`}
                show={showDialog === 'revoke-access'}
                saveLabel='Revoke access'
                onSave={() => onRevokeAccess(selected, setShowDialog)}
                onCancel={() => {
                    setShowDialog('');
                }}
                onClose={() => setShowDialog('')}
            />
        </Drawer>
    );
}

export default Delegates;
